import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, copyText } from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/SectionTitle";
import { PlayoffBracketView } from "@/components/PlayoffBracketView";
import { TreeStructure, ShareNetwork } from "@phosphor-icons/react";

export default function Playoffs() {
  const [data, setData] = useState({ divisions: [], brackets: {} });
  const [active, setActive] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/playoffs").then((r) => {
      setData(r.data);
      const wanted = params.get("division");
      const valid = (r.data.divisions || []).some((d) => d.key === wanted);
      if (valid) setActive(wanted);
      else if (r.data.divisions?.length) setActive(r.data.divisions[0].key);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const selectDivision = (key) => {
    setActive(key);
    setParams({ division: key }, { replace: true });
  };

  const bracket = active ? data.brackets[active] : null;
  const activeLabel = data.divisions.find((d) => d.key === active)?.label || "";

  const share = async () => {
    if (!active) return;
    const link = `${API}/share/playoffs?division=${encodeURIComponent(active)}`;
    if (await copyText(link)) toast.success(`${activeLabel} playoffs link copied — paste it on Facebook or Instagram!`);
    else toast.error("Could not copy link");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="playoffs-page">
      <SectionTitle overline="Win or go home" title="Playoffs" sub="Single-elimination brackets by division. Higher seeds get byes; winners advance automatically as results come in." />

      <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="playoffs-division-tabs">
        {data.divisions.map((d) => (
          <button
            key={d.key}
            data-testid={`playoffs-tab-${d.key.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => selectDivision(d.key)}
            className={`px-6 py-2.5 text-sm font-bold uppercase tracking-wide transition-all duration-200 ${
              active === d.key ? "bg-[#FF5722] text-black" : "border border-white/15 text-white/70 hover:text-white"
            }`}
          >
            {d.label}
          </button>
        ))}
        <button onClick={share} data-testid="playoffs-share-btn" className="ml-auto flex items-center gap-1.5 border border-[#FF5722]/50 text-[#FF5722] hover:bg-[#FF5722]/10 uppercase tracking-wide text-xs font-bold px-4 py-2.5 transition-all duration-200">
          <ShareNetwork size={15} weight="bold" /> Share {activeLabel}
        </button>
      </div>

      {bracket
        ? <PlayoffBracketView bracket={bracket} />
        : (
          <div className="border-2 border-dashed border-white/15 py-20 text-center" data-testid="playoffs-empty">
            <TreeStructure size={44} className="text-white/25 mx-auto mb-4" />
            <p className="text-white/50">The {activeLabel} playoff bracket hasn't been set yet. Check back once seeding is finalized!</p>
          </div>
        )}
    </div>
  );
}
